import { useMemo } from 'react';
import { useMyPageDashboard } from '../hooks/useMyPageDashboard';
import { getPremiumProduct } from './catalog';

function hasRequirement(stats, requirement) {
    if (!requirement) return true;

    switch (requirement.testType) {
        case 'mega':
            return Boolean(stats?.mega);
        case 'love':
            return Boolean(stats?.love);
        case 'beyond':
            return Boolean(stats?.beyond);
        case 'inside':
            return Boolean(stats?.mega && stats?.beyond);
        default:
            return false;
    }
}

export function usePremiumAccess(productId) {
    const dashboard = useMyPageDashboard();
    const product = getPremiumProduct(productId);

    return useMemo(() => {
        const accessMode = product?.accessMode || 'login_required';
        const loggedIn = dashboard.loggedIn;
        const statsSource = loggedIn
            ? dashboard.stats
            : dashboard.guestData?.localStats;
        const requirementMet = hasRequirement(statsSource, product?.requirement);
        const paymentProductId = product?.paymentProductId || null;
        const entitlementGranted = paymentProductId
            ? Boolean(dashboard.entitlements?.[paymentProductId])
            : true;

        const needsLogin = !loggedIn && accessMode !== 'guest_or_login';
        const needsPayment = !needsLogin && requirementMet && !entitlementGranted;
        const paymentLabel = product?.price
            ? `Unlock for KRW ${product.price.toLocaleString()}`
            : 'Unlock premium';

        return {
            ...dashboard,
            product,
            accessMode,
            requirementMet,
            entitlementGranted,
            canAccess: !needsLogin && !needsPayment,
            needsLogin,
            needsPayment,
            paymentProductId,
            paymentLabel,
            ctaRoute: needsPayment
                ? '/premium-payment'
                : (product?.requirement?.ctaRoute || '/'),
            ctaLabel: needsPayment
                ? paymentLabel
                : (product?.requirement?.ctaLabel || 'Continue'),
            secondaryCtaRoute: product?.requirement?.secondaryCtaRoute || null,
            secondaryCtaLabel: product?.requirement?.secondaryCtaLabel || null,
            missingMessage: needsPayment
                ? `Payment is required to unlock ${product?.title || 'this premium content'}.`
                : (product?.requirement?.missingMessage || 'Required results are missing.'),
        };
    }, [dashboard, product]);
}
